/**
 * ============================================================================
 * WEEK CONTROLS — Reporting Week / Last X / Forward X (QuickSight parity).
 * ============================================================================
 *
 * Mirrors the QuickSight controls "Report Period", "Last X Historical
 * Periods" and "Next X Future Periods". Changing any control changes the
 * getMetrics durationRange (Redux → RTK cache key) → new API call.
 *
 * - Reporting Week options come from getWeekNumbers, newest-first, capped at
 *   the current week (live) / the week before it (snapshot — captures exist
 *   only for completed weeks). Week tokens are Canonical ("2026 Week 06")
 *   straight from the API — displayed as-is.
 * - Forward X is hidden in snapshot mode: a snapshot records the past.
 */

import React from 'react';
import FormField from '@amzn/awsui-components-core/form-field';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import {
  reportingWeekChanged,
  lastXWeeksChanged,
  forwardXWeeksChanged,
} from 'src/features/dashboard/dashboardSlice';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { weekBefore } from 'src/features/api/durationRange';

const X_OPTIONS = [2, 4, 8, 12].map((n) => ({ label: String(n), value: String(n) }));

export const WeekControls: React.FC = () => {
  const dispatch = useAppDispatch();
  const dataMode = useAppSelector((s) => s.dashboard.dataMode);
  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  const lastXWeeks = useAppSelector((s) => s.dashboard.lastXWeeks);
  const forwardXWeeks = useAppSelector((s) => s.dashboard.forwardXWeeks);

  const { data: weekData } = useGetWeekNumbersQuery();

  // Anchor cap: you can't report on a week that hasn't happened. Live mode
  // anchors at most on the CURRENT week; snapshot mode on the week BEFORE it
  // (captures are taken at week close, so the running week has none). The
  // getWeekNumbers universe includes +4 forward weeks — those are for the
  // Forward X columns, never anchor options.
  const isSnapshot = dataMode === 'snapshot';
  const weeks = weekData?.weeks ?? [];
  const newestAnchor = weekData
    ? isSnapshot
      ? weekBefore(weeks, weekData.currentWeek)
      : weekData.currentWeek
    : undefined;
  const newestAnchorIndex = newestAnchor ? weeks.indexOf(newestAnchor) : -1;

  // Options newest-first (recent weeks are the ones people anchor on); the
  // newest anchor is the pinned default pseudo-entry — don't list it twice.
  const weekOptions = (newestAnchorIndex === -1 ? weeks : weeks.slice(0, newestAnchorIndex))
    .slice()
    .reverse()
    .map((w) => ({
      label: w,
      value: w,
    }));

  const defaultWeekOption = newestAnchor
    ? {
        label: `${newestAnchor} (${isSnapshot ? 'latest snapshot' : 'current'})`,
        value: '',
      }
    : { label: 'Current week', value: '' };

  const selectedWeekOption = reportingWeek
    ? weekOptions.find((o) => o.value === reportingWeek) ?? defaultWeekOption
    : defaultWeekOption;

  return (
    <SpaceBetween direction="horizontal" size="s">
      <FormField label="Reporting week">
        <Select
          selectedOption={selectedWeekOption}
          options={[defaultWeekOption, ...weekOptions]}
          onChange={({ detail }) =>
            dispatch(reportingWeekChanged(detail.selectedOption.value || null))
          }
          filteringType="auto"
        />
      </FormField>
      <FormField label="Last X weeks">
        <Select
          selectedOption={{ label: String(lastXWeeks), value: String(lastXWeeks) }}
          options={X_OPTIONS}
          onChange={({ detail }) =>
            dispatch(lastXWeeksChanged(parseInt(detail.selectedOption.value!, 10)))
          }
        />
      </FormField>
      {dataMode === 'live' && (
        <FormField label="Forward X weeks">
          <Select
            selectedOption={{ label: String(forwardXWeeks), value: String(forwardXWeeks) }}
            options={X_OPTIONS}
            onChange={({ detail }) =>
              dispatch(forwardXWeeksChanged(parseInt(detail.selectedOption.value!, 10)))
            }
          />
        </FormField>
      )}
    </SpaceBetween>
  );
};
